/**
 * Workflow Executor
 * Handles complex multi-step commands and compound actions
 * Enables commands like "show overdue wells and create route"
 */

import { actionExecutor, AppContext, ExecutionResult } from './ActionExecutor';
import { contextManager } from './ContextManager';
import { openAIService } from './openaiService';

export interface WorkflowStep {
  id: string;
  action: string;
  params: any;
  dependsOn?: string[]; // IDs of steps that must complete first
  condition?: (results: Map<string, any>) => boolean;
  timeout?: number; // Max execution time in ms
  retryable?: boolean;
  description?: string;
}

export interface WorkflowDefinition {
  id: string;
  name: string;
  description: string;
  steps: WorkflowStep[];
  estimatedTime?: number;
  requiresConfirmation?: boolean;
}

export interface WorkflowResult {
  success: boolean;
  workflowId?: string;
  steps: number;
  completed: number;
  failed: number;
  results: Map<string, any>;
  errors: Array<{ stepId: string; error: string }>;
  executionTime: number;
}

class WorkflowExecutor {
  private static instance: WorkflowExecutor;
  private workflows: Map<string, WorkflowDefinition> = new Map();
  private runningWorkflows: Map<string, { cancel: () => void }> = new Map();

  private constructor() {
    this.registerDefaultWorkflows();
  }

  static getInstance(): WorkflowExecutor {
    if (!WorkflowExecutor.instance) {
      WorkflowExecutor.instance = new WorkflowExecutor();
    }
    return WorkflowExecutor.instance;
  }

  /**
   * Execute a workflow from natural language command
   */
  async executeWorkflow(
    command: string,
    context: AppContext
  ): Promise<WorkflowResult> {
    const startTime = performance.now();

    try {
      // 1. Parse command into workflow steps
      const workflow = await this.parseWorkflow(command, context);
      
      if (!workflow || workflow.steps.length === 0) {
        return {
          success: false,
          steps: 0,
          completed: 0,
          failed: 0,
          results: new Map(),
          errors: [{ stepId: 'parse', error: 'Could not parse workflow' }],
          executionTime: performance.now() - startTime
        };
      }

      // 2. Execute workflow
      return await this.executeWorkflowDefinition(workflow, context);

    } catch (error) {
      console.error('Workflow execution failed:', error);
      return {
        success: false,
        steps: 0,
        completed: 0,
        failed: 1,
        results: new Map(),
        errors: [{ 
          stepId: 'workflow', 
          error: error instanceof Error ? error.message : 'Unknown error' 
        }],
        executionTime: performance.now() - startTime
      };
    }
  }

  /**
   * Execute a predefined workflow
   */
  async executeWorkflowDefinition(
    workflow: WorkflowDefinition,
    context: AppContext
  ): Promise<WorkflowResult> {
    const startTime = performance.now();
    const results = new Map<string, any>();
    const errors: Array<{ stepId: string; error: string }> = [];
    let completed = 0;
    let failed = 0;


    for (const step of workflow.steps) {
      try {
        // Check dependencies
        if (step.dependsOn && step.dependsOn.length > 0) {
          const depsReady = step.dependsOn.every(depId => results.has(depId));
          if (!depsReady) {
            failed++;
            errors.push({ 
              stepId: step.id, 
              error: 'Dependencies not met' 
            });
            continue;
          }
        }

        // Check condition
        if (step.condition && !step.condition(results)) {
          continue;
        }

        // Resolve params with previous results
        const resolvedParams = this.resolveParams(step.params, results);

        // Execute with timeout
        const stepPromise = actionExecutor.executeCommand(
          step.description || '',
          context,
          step.action
        );

        const timeoutPromise = step.timeout 
          ? new Promise<ExecutionResult>((_, reject) => 
              setTimeout(() => reject(new Error('Step timeout')), step.timeout)
            )
          : null;

        const result = timeoutPromise
          ? await Promise.race([stepPromise, timeoutPromise])
          : await stepPromise;

        if (result.success) {
          results.set(step.id, result.data);
          completed++;
        } else {
          failed++;
          errors.push({ 
            stepId: step.id, 
            error: result.error || 'Step failed' 
          });
          console.error(`❌ Step ${step.id} failed:`, result.error);

          // Stop workflow if step is not optional and fails
          if (!step.retryable) {
            break;
          }
        }

      } catch (error) {
        failed++;
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        errors.push({ stepId: step.id, error: errorMsg });
        console.error(`❌ Step ${step.id} threw error:`, error);

        if (!step.retryable) {
          break;
        }
      }
    }

    const executionTime = performance.now() - startTime;

    return {
      success: failed === 0,
      workflowId: workflow.id,
      steps: workflow.steps.length,
      completed,
      failed,
      results,
      errors,
      executionTime
    };
  }

  /**
   * Parse natural language command into workflow steps
   */
  private async parseWorkflow(
    command: string,
    context: AppContext
  ): Promise<WorkflowDefinition | null> {
    // Check if it's a registered workflow first
    const registered = this.matchRegisteredWorkflow(command);
    if (registered) return registered;

    // Use GPT-4 to parse complex commands
    try {
      const availableActions = actionExecutor.getAllActions().map(a => ({
        id: a.id,
        type: a.type,
        target: a.target,
        examples: a.examples.slice(0, 2)
      }));

      const prompt = `Break this command into executable workflow steps.

Command: "${command}"

Current context:
- Screen: ${context.currentScreen}
- Selected well: ${context.selectedWell?.name || 'none'}
- Available wells: ${context.wells?.length || 0}

Available actions:
${JSON.stringify(availableActions, null, 2)}

Return a JSON workflow definition with these properties:
{
  "id": "unique_id",
  "name": "Workflow name",
  "description": "What this workflow does",
  "steps": [
    {
      "id": "step_1",
      "action": "action_id",
      "params": { },
      "dependsOn": [],
      "description": "Step description"
    }
  ]
}

Rules:
1. Use actual action IDs from the available actions list
2. Steps can depend on previous step results using dependsOn
3. Use $RESULT_X to reference result from step with id 'X'
4. Keep workflows concise (max 5 steps)`;

      const response = await openAIService.chat(prompt);
      const workflow = JSON.parse(response.response);
      
      return workflow as WorkflowDefinition;

    } catch (error) {
      console.error('Failed to parse workflow:', error);
      return null;
    }
  }

  /**
   * Match command to registered workflow
   */
  private matchRegisteredWorkflow(command: string): WorkflowDefinition | null {
    const lower = command.toLowerCase();

    for (const workflow of this.workflows.values()) {
      if (lower.includes(workflow.name.toLowerCase())) {
        return workflow;
      }
    }

    return null;
  }

  /**
   * Resolve parameters using previous step results
   */
  private resolveParams(
    params: any,
    results: Map<string, any>
  ): any {
    if (typeof params !== 'object' || params === null) {
      return params;
    }

    const resolved: any = Array.isArray(params) ? [] : {};

    for (const [key, value] of Object.entries(params)) {
      if (typeof value === 'string' && value.startsWith('$RESULT_')) {
        const resultId = value.substring(8); // Remove '$RESULT_'
        resolved[key] = results.get(resultId);
      } else if (typeof value === 'object') {
        resolved[key] = this.resolveParams(value, results);
      } else {
        resolved[key] = value;
      }
    }

    return resolved;
  }

  /**
   * Register a workflow
   */
  registerWorkflow(workflow: WorkflowDefinition): void {
    this.workflows.set(workflow.id, workflow);
  }

  /**
   * Register default workflows
   */
  private registerDefaultWorkflows(): void {
    // Workflow: Show overdue wells and create route
    this.registerWorkflow({
      id: 'overdue_wells_route',
      name: 'Overdue Wells Route',
      description: 'Show overdue wells and create optimized route',
      steps: [
        {
          id: 'filter_overdue',
          action: 'filter_wells',
          params: { status: 'overdue' },
          description: 'Filter overdue wells'
        },
        {
          id: 'create_route',
          action: 'create_route',
          params: { wells: '$RESULT_filter_overdue' },
          dependsOn: ['filter_overdue'],
          description: 'Create route from filtered wells'
        }
      ]
    });

    // Workflow: Morning startup routine
    this.registerWorkflow({
      id: 'morning_routine',
      name: 'Morning Routine',
      description: 'Start daily routine with route and notifications',
      steps: [
        {
          id: 'check_alerts',
          action: 'show_alerts',
          params: { unread: true },
          description: 'Check unread alerts'
        },
        {
          id: 'show_route',
          action: 'show_route',
          params: { },
          description: 'Show daily route'
        },
        {
          id: 'navigate_first',
          action: 'navigate_to_first_well',
          params: { route: '$RESULT_show_route' },
          dependsOn: ['show_route'],
          description: 'Navigate to first well'
        }
      ]
    });

    // Workflow: Bulk maintenance scheduling
    this.registerWorkflow({
      id: 'bulk_maintenance',
      name: 'Bulk Maintenance',
      description: 'Schedule maintenance for multiple wells',
      steps: [
        {
          id: 'filter_wells',
          action: 'filter_wells',
          params: { needsMaintenance: true },
          description: 'Find wells needing maintenance'
        },
        {
          id: 'create_tickets',
          action: 'bulk_create_tickets',
          params: { 
            wells: '$RESULT_filter_wells',
            type: 'preventive_maintenance',
            priority: 'medium'
          },
          dependsOn: ['filter_wells'],
          description: 'Create maintenance tickets'
        },
        {
          id: 'create_schedule',
          action: 'create_schedule',
          params: { tickets: '$RESULT_create_tickets' },
          dependsOn: ['create_tickets'],
          description: 'Generate maintenance schedule'
        }
      ]
    });

    // Workflow: End of day summary
    this.registerWorkflow({
      id: 'end_of_day',
      name: 'End of Day',
      description: 'Complete end of day routine',
      steps: [
        {
          id: 'incomplete_entries',
          action: 'show_drafts',
          params: { },
          description: 'Check for incomplete entries'
        },
        {
          id: 'pending_tickets',
          action: 'show_my_tickets',
          params: { status: 'open' },
          description: 'Review open tickets'
        },
        {
          id: 'generate_report',
          action: 'generate_daily_report',
          params: { },
          description: 'Generate daily summary'
        }
      ]
    });

  }

  /**
   * Get all registered workflows
   */
  getAllWorkflows(): WorkflowDefinition[] {
    return Array.from(this.workflows.values());
  }

  /**
   * Get workflow by ID
   */
  getWorkflow(id: string): WorkflowDefinition | undefined {
    return this.workflows.get(id);
  }
}

// Export singleton
export const workflowExecutor = WorkflowExecutor.getInstance();
